import { useEffect, useState } from "react";
import { taxApi } from "./api";
import type { TaxConfig, TaxConfigResponse } from "./types";
import { usePermission } from "@/lib/permissions/useCan";
import { PERMISSIONS } from "@/lib/permissions/catalog";

/**
 * Configuración del módulo tributario.
 *
 * Las credenciales del proveedor NO se editan acá: viven en Secret Manager.
 * Esta pantalla cubre lo que cambia sin redeploy y que alguien de Finanzas
 * necesita poder corregir — los datos que salen impresos en cada documento.
 */

const CAMPOS: Array<{
  key: keyof TaxConfig["emisor"];
  label: string;
  hint?: string;
  numeric?: boolean;
}> = [
  { key: "rut", label: "RUT", hint: "Se valida el dígito verificador al guardar." },
  { key: "razonSocial", label: "Razón social" },
  { key: "giro", label: "Giro", hint: "Sale impreso en cada boleta." },
  { key: "direccion", label: "Dirección" },
  { key: "comuna", label: "Comuna" },
  { key: "actividadEconomica", label: "Acteco", hint: "Código de actividad del SII.", numeric: true },
];

export function TaxConfigScreen() {
  const puedeEditar = usePermission(PERMISSIONS.TAX_CONFIGURE);

  const [data, setData] = useState<TaxConfigResponse | null>(null);
  const [form, setForm] = useState<TaxConfig | null>(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [msg, setMsg] = useState<{ tipo: "ok" | "error"; texto: string } | null>(null);

  useEffect(() => {
    taxApi
      .getConfig()
      .then((r) => {
        setData(r);
        setForm(r.config);
      })
      .catch(() => setMsg({ tipo: "error", texto: "No se pudo cargar la configuración." }))
      .finally(() => setCargando(false));
  }, []);

  const guardar = async () => {
    if (!form) return;
    setGuardando(true);
    setMsg(null);
    try {
      const { config } = await taxApi.updateConfig({
        emisor: form.emisor,
        indServicio: form.indServicio,
      });
      setForm(config);
      setMsg({ tipo: "ok", texto: "Configuración guardada." });
    } catch (e: any) {
      // El backend devuelve el motivo (ej. RUT inválido); mostrarlo tal cual
      // evita el clásico "ocurrió un error" que no dice nada.
      setMsg({
        tipo: "error",
        texto: e?.response?.data?.error || "No se pudo guardar.",
      });
    } finally {
      setGuardando(false);
    }
  };

  if (cargando) return <p className="text-fg-muted">Cargando configuración…</p>;
  if (!form || !data) return <p className="text-danger">{msg?.texto || "Sin datos."}</p>;

  const { estado } = data;
  const enMock = estado.provider === "mock";

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="nx-h1">Configuración tributaria (SII)</h1>
        <p className="text-sm text-fg-muted mt-1">
          Datos que salen impresos en cada documento. Las credenciales del
          proveedor no se editan acá: viven en Secret Manager.
        </p>
      </div>

      {/* Estado — la pregunta que trae a alguien a esta pantalla no es
          "cuál es el giro" sino "por qué no se está emitiendo". */}
      <section className="nx-card p-4 flex flex-col gap-2">
        <span className="nx-eyebrow">Estado</span>
        <Fila
          etiqueta="Emisión"
          valor={estado.emisionActiva ? "Activa" : "Apagada"}
          alerta={!estado.emisionActiva}
        />
        <Fila
          etiqueta="Proveedor"
          valor={enMock ? `${estado.provider} — folios ficticios` : estado.provider}
          alerta={enMock}
        />
        <Fila
          etiqueta="Credenciales"
          valor={estado.credencialesCargadas ? "Cargadas" : "Faltan"}
          alerta={!estado.credencialesCargadas}
        />
        <Fila etiqueta="Producto" valor={estado.producto} />
        {!estado.emisionActiva && (
          <p className="text-xs text-fg-muted mt-1">
            Se enciende con la flag <code>DTE_EMISSION</code> en Operaciones ›
            Feature Flags. Mientras esté apagada no se encola ni emite nada.
          </p>
        )}
      </section>

      <section className="nx-card p-4 flex flex-col gap-4">
        <span className="nx-eyebrow">Emisor</span>
        <div className="grid gap-4 sm:grid-cols-2">
          {CAMPOS.map((c) => (
            <label key={c.key} className="flex flex-col gap-1">
              <span className="text-sm font-medium">{c.label}</span>
              <input
                className="nx-input"
                value={String(form.emisor[c.key] ?? "")}
                disabled={!puedeEditar || guardando}
                onChange={(e) =>
                  setForm({
                    ...form,
                    emisor: {
                      ...form.emisor,
                      [c.key]: c.numeric ? Number(e.target.value) || 0 : e.target.value,
                    },
                  })
                }
              />
              {c.hint && <span className="text-xs text-fg-muted">{c.hint}</span>}
            </label>
          ))}
        </div>
      </section>

      <section className="nx-card p-4 flex flex-col gap-3">
        <span className="nx-eyebrow">Emisión</span>
        <span className="text-sm font-medium">Tipo de boleta (IndServicio)</span>
        {([3, 4] as const).map((v) => (
          <label key={v} className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio"
              name="indServicio"
              checked={form.indServicio === v}
              disabled={!puedeEditar || guardando}
              onChange={() => setForm({ ...form, indServicio: v })}
              className="mt-1"
            />
            <span className="text-sm">
              <strong>{v}</strong> ·{" "}
              {v === 3 ? "Venta propia" : "Espectáculos por cuenta de terceros"}
              {v === 4 && (
                <em className="block text-xs text-warning mt-0.5">
                  Requiere la resolución del SII que autoriza el mandato.
                  Activarlo sin ella declara una condición que no se tiene.
                </em>
              )}
            </span>
          </label>
        ))}
      </section>

      <div className="flex items-center justify-end gap-3">
        {msg && (
          <span className={msg.tipo === "ok" ? "text-success text-sm" : "text-danger text-sm"}>
            {msg.texto}
          </span>
        )}
        {!puedeEditar && (
          <span className="text-xs text-fg-muted">
            Necesitas el permiso <code>tax:configure</code> para editar.
          </span>
        )}
        <button
          className="nx-btn-primary"
          onClick={guardar}
          disabled={!puedeEditar || guardando}
        >
          {guardando ? "Guardando…" : "Guardar"}
        </button>
      </div>
    </div>
  );
}

function Fila({
  etiqueta,
  valor,
  alerta,
}: {
  etiqueta: string;
  valor: string;
  alerta?: boolean;
}) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-fg-muted">{etiqueta}</span>
      <span className={alerta ? "text-warning font-medium" : "font-medium"}>{valor}</span>
    </div>
  );
}
